import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { T, t } from '@sm/intl';
import { startCase } from 'lodash';
import { Button } from '@wds/button';
import { Modal } from '@wds/modal';
import { Typography } from '@wds/typography';
import { useGeneral, useGeneralActions, useThemesReuseActions, useThemesReuse } from '../../../Context';
import { USE_CASE_NONE, USE_CASES } from '../../../constants';
import { trackAmplitudeEvent } from '../../../lib';
import { generateOecSuggestedThemes } from '../../../graph/generateSuggestedThemes';
export const COPY = {
  header: {
    id: 'OpenEndedCoding.ChangeUseCaseModal.header',
    defaultMessage: 'Confirm Use Case',
    description: '[Type: Header][Vis: med] - Header for Change Use Case Modal'
  },
  headerNone: {
    id: 'OpenEndedCoding.ChangeUseCaseModal.headerNone',
    defaultMessage: 'Hide suggested themes',
    description: '[Type: Header][Vis: med] - Header for Change Use Case Modal when user selects none'
  },
  description: {
    id: 'OpenEndedCoding.ChangeUseCaseModal.description',
    defaultMessage: 'You are going to generate new suggested themes based on this new use case, {displayedUseCase}.',
    description: '[Type: Header][Vis: med] - Change Use Case Modal description'
  },
  descriptionNone: {
    id: 'OpenEndedCoding.ChangeUseCaseModal.descriptionNone',
    defaultMessage: 'This action will hide all suggested themes for your responses. Are you sure?',
    description: '[Type: Header][Vis: med] - Change Use Case Modal description when user selects none'
  },
  cancel: {
    id: 'OpenEndedCoding.ChangeUseCaseModal.cancel',
    defaultMessage: 'Cancel',
    description: '[Type: Header][Vis: med] - Change Use Case Modal cancel button'
  },
  change: {
    id: 'OpenEndedCoding.ChangeUseCaseModal.change',
    defaultMessage: 'Update',
    description: '[Type: Header][Vis: med] - Change Use Case Modal update button'
  }
};
const ChangeUseCaseModal = ({
  show,
  previousUseCase,
  onClose
}) => {
  const {
    surveyId,
    questionId,
    isSolutions
  } = useGeneral();
  const {
    setError
  } = useGeneralActions();
  const {
    setSelectedUseCase
  } = useThemesReuseActions();
  const {
    selectedUseCase
  } = useThemesReuse();
  const displayedUseCase = selectedUseCase && startCase(selectedUseCase);
  // just default to none if no confirmed use case
  // we won't fire the mutation if set to none
  const domain = selectedUseCase && selectedUseCase in USE_CASES ? USE_CASES[selectedUseCase] : USE_CASES.none;
  const [generateOECSuggestedThemes] = useMutation(generateOecSuggestedThemes, {
    onCompleted: ({
      generateOecSuggestedTags: {
        success
      }
    }) => {
      // error occurred
      if (!success) {
        setError(true);
      }
    },
    onError: () => {
      setError(true);
    }
  });
  return _jsxs(Modal, {
    "data-testid": "ChangeUseCaseModal__OEC",
    show: show,
    onClose: () => {
      onClose();
      setSelectedUseCase(previousUseCase);
    },
    children: [_jsx(Modal.Header, {
      header: previousUseCase === USE_CASE_NONE ? t(COPY.headerNone) : t(COPY.header)
    }), _jsx(Modal.Body, {
      children: _jsx(Typography, {
        component: "span",
        variant: "bodySm",
        children: selectedUseCase === USE_CASE_NONE ? _jsx(T, {
          desc: COPY.descriptionNone
        }) : _jsx(T, {
          html: true,
          desc: COPY.description,
          values: {
            displayedUseCase
          }
        })
      })
    }), _jsxs(Modal.Footer, {
      align: "right",
      children: [_jsx(Button, {
        variant: "ghost",
        color: "secondary",
        onClick: () => {
          onClose();
          setSelectedUseCase(previousUseCase);
        },
        children: _jsx(T, {
          desc: COPY.cancel
        })
      }), _jsx(Button, {
        color: "link",
        onClick: () => {
          if (selectedUseCase !== USE_CASE_NONE) {
            // generate suggested themes for the use case
            void generateOECSuggestedThemes({
              variables: {
                input: {
                  surveyId,
                  questionId,
                  domain
                }
              }
            });
          }
          onClose();
          // tracking
          trackAmplitudeEvent({
            amplitudeEvent: 'open-ended coding change use case',
            data: {
              surveyId,
              questionId,
              pageName: isSolutions ? 'ctm' : 'analyze'
            }
          });
        },
        children: _jsx(T, {
          desc: COPY.change
        })
      })]
    })]
  });
};
export default ChangeUseCaseModal;